import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import {
  endorsementWizard,
  type EndorsementChange,
  type EndorsementDetail,
  type PolicyDetail,
} from '@/shared/api/resources';
import {
  formatCivilDate,
  formatDocument,
  formatMoney,
  formatRate,
} from '@/shared/lib/format';
import { useAuth } from '@/shared/auth/useAuth';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { Card, CardBody, CardHeader } from '@/shared/ui/Card';
import { StatusBadge } from '@/shared/ui/StatusBadge';

import { rotulosDosCampos, valorEmTexto } from './endosso';
import { regrasEmTexto } from './endossoFinanceiro';
import {
  EndorsementAutomaticApprovals,
  EndorsementCoinsuranceObject,
  EndorsementQuoteFiles,
} from './EndorsementFinancialSummary';
import { EndorsementInfo } from './EndorsementPolicyDetails';
import { RefundAccountSection } from './RefundAccountSection';

/**
 * Passo "Resumo da alteracao" (END-009, END-074, END-078, END-086 a END-091, END-099,
 * END-100, END-108).
 *
 * O comparativo do antes e do depois, a confirmacao obrigatoria e o envio ("Gerar
 * cotacao"). No fluxo da esteira, o envio abre a operacao do tipo endosso; o link "Ir
 * para timeline" leva a linha do tempo dela.
 *
 * P-END-2: no endosso de alteracao e no de cancelamento, os dados do endosso, os dados
 * bancarios da restituicao, o objeto do cosseguro e, depois do envio, as aprovacoes
 * automaticas, o "Baixar resumo" e o "Gerar minuta" da cotacao de endosso. O envio
 * abre a cotacao; a emissao espera a regra do premio e da restituicao.
 */
export function ChangeSummaryStep({
  policy,
  endorsement,
  editavel,
  confirmando,
  enviando,
  onConfirmar,
  onVoltar,
  onGerar,
  onIrParaEmissao,
}: {
  policy: PolicyDetail;
  endorsement: EndorsementDetail;
  editavel: boolean;
  confirmando: boolean;
  enviando: boolean;
  onConfirmar: (confirmado: boolean) => void;
  onVoltar: () => void;
  onGerar: () => void;
  onIrParaEmissao: () => void;
}) {
  const { can } = useAuth();
  const codigo = endorsement.endorsement_type?.code ?? 'neutro';
  const comparativo = useQuery({
    queryKey: ['endorsement-changes', endorsement.id, endorsement.changes?.length ?? 0],
    queryFn: ({ signal }) => endorsementWizard.changes(endorsement.id, false, signal),
    enabled: codigo === 'neutro',
    retry: false,
  });
  const problemas = endorsement.problems ?? [];
  const enviado = endorsement.status !== 'rascunho';
  const financeiro = endorsement.financial ?? null;
  // So o aniversario continua com o envio bloqueado (P-PAR-3); alteracao e
  // cancelamento abrem a cotacao de endosso e esperam a regra na emissao (P-END-2).
  const envioBloqueado = (endorsement.calculation_pending ?? false) && !financeiro;
  const emAndamento = ['rascunho', 'solicitado', 'em_analise'].includes(
    endorsement.status,
  );
  // A conta da restituicao pode ser trocada enquanto o endosso esta em andamento: ela
  // pode sair do cadastro do tomador durante a analise.
  const podeTrocarConta = can('endossos.endosso.create') && emAndamento;

  return (
    <div className="flex flex-col gap-4">
      {codigo === 'neutro' ? (
        <Card>
          <CardHeader
            title="Dados do endosso neutro"
            description="O antes e o depois de cada dado."
          />
          <CardBody className="grid gap-6 lg:grid-cols-2">
            <BlocoDaParte
              titulo="Tomador"
              documento={policy.parties?.policyholder?.document ?? ''}
              linhas={(comparativo.data ?? []).filter(
                (item) => item.scope === 'tomador',
              )}
            />
            {policy.parties?.insured ? (
              <BlocoDaParte
                titulo="Segurado"
                documento={policy.parties.insured.document}
                linhas={(comparativo.data ?? []).filter(
                  (item) => item.scope === 'segurado',
                )}
              />
            ) : null}
          </CardBody>
        </Card>
      ) : null}

      {codigo === 'alteracao' || codigo === 'aniversario' ? (
        <Card>
          <CardHeader title="Dados do endosso de alteracao" />
          <CardBody className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <EndorsementInfo
              rotulo="Premio a cobrar / restituir"
              valor={`Regra pendente (${regrasEmTexto(financeiro?.premium_rules)})`}
            />
            {(
              [
                'insured_amount',
                'coverage_start',
                'coverage_end',
                'complementary_insured_amount',
                'particular_clause',
              ] as const
            ).map((campo) => (
              <EndorsementInfo
                key={campo}
                rotulo={rotulosDosCampos[campo]}
                valor={valorEmTexto(
                  (endorsement.changes ?? []).find((item) => item.field === campo)
                    ?.after,
                )}
              />
            ))}
            <EndorsementInfo
              rotulo="Prazo"
              valor="Nao calculado: a contagem esta pendente (PRC-008)"
            />
            <EndorsementInfo
              rotulo="Taxa aplicada (%)"
              valor={formatRate(policy.applied_rate_bps)}
            />
            <EndorsementInfo
              rotulo="Objeto da apolice"
              valor={endorsement.object_text ?? ''}
            />
          </CardBody>
        </Card>
      ) : null}

      {codigo === 'cancelamento' ? (
        <Card>
          <CardHeader title="Dados do endosso de cancelamento" />
          <CardBody className="grid gap-4 sm:grid-cols-3">
            <EndorsementInfo
              rotulo="Data de cancelamento"
              valor={
                financeiro?.effective_date
                  ? formatCivilDate(financeiro.effective_date)
                  : 'Nao informada'
              }
            />
            <EndorsementInfo
              rotulo="Valor estimado a restituir"
              valor={
                financeiro?.estimated_refund
                  ? formatMoney(financeiro.estimated_refund)
                  : `Regra pendente (${regrasEmTexto(financeiro?.premium_rules)})`
              }
            />
            <EndorsementInfo
              rotulo="Revisao de valor solicitada"
              valor={financeiro?.review_requested ? 'Sim' : 'Nao'}
            />
          </CardBody>
        </Card>
      ) : null}

      <Card>
        <CardHeader title="Motivo e documentos" />
        <CardBody className="grid gap-4 sm:grid-cols-2">
          <EndorsementInfo
            rotulo="Motivo do endosso"
            valor={endorsement.endorsement_reason?.name ?? ''}
          />
          <EndorsementInfo rotulo="Observacao" valor={endorsement.reason} />
          <EndorsementInfo
            rotulo="Documentos"
            valor={(endorsement.documents ?? [])
              .map(
                (item) =>
                  `${item.title} (${item.current_version > 0 ? 'enviado' : 'pendente'})`,
              )
              .join('; ')}
          />
        </CardBody>
      </Card>

      {codigo === 'cancelamento' && financeiro ? (
        <RefundAccountSection endorsement={endorsement} editavel={podeTrocarConta} />
      ) : null}

      {financeiro ? <EndorsementCoinsuranceObject endorsement={endorsement} /> : null}

      {envioBloqueado ? (
        <Alert tone="warning" title="Envio bloqueado pela regra pendente">
          Este endosso movimenta premio ou restitui, e o calculo ainda nao foi
          homologado. O rascunho fica guardado ate a regra entrar.
        </Alert>
      ) : null}
      {financeiro ? (
        <Alert tone="info" title="A emissao espera a regra">
          {financeiro.issuance_rules.message} Regras pendentes:{' '}
          {regrasEmTexto(financeiro.issuance_rules)}.
        </Alert>
      ) : null}

      {!enviado && problemas.length > 0 ? (
        <Alert tone="warning" title="Antes de gerar a cotacao">
          <ul className="list-disc pl-4">
            {problemas.map((problema) => (
              <li key={`${problema.field}-${problema.message}`}>{problema.message}</li>
            ))}
          </ul>
        </Alert>
      ) : null}

      {editavel ? (
        <label className="flex items-center gap-2 text-sm text-ink-900">
          <input
            type="checkbox"
            checked={endorsement.summary_confirmed ?? false}
            disabled={confirmando}
            onChange={(event) => onConfirmar(event.target.checked)}
            className="size-4 rounded border-line-strong text-brand-500"
          />
          Por favor, confirme que revisou todas as informacoes antes de finalizar.
        </label>
      ) : null}

      {enviado && financeiro ? (
        <>
          <EndorsementAutomaticApprovals endorsement={endorsement} />
          <EndorsementQuoteFiles endorsement={endorsement} />
        </>
      ) : null}

      {enviado ? (
        <Alert tone="info" title="Cotacao de endosso gerada">
          <div className="flex flex-wrap items-center gap-2">
            <span>Situacao:</span>
            <StatusBadge kind="situacaoEndosso" value={endorsement.status} />
            {endorsement.operation ? (
              <Link
                to={`/operacoes/${endorsement.operation.id}?aba=follow-up`}
                className="text-brand-600 underline-offset-2 hover:underline"
              >
                Ir para timeline
              </Link>
            ) : null}
          </div>
        </Alert>
      ) : null}

      <div className="flex flex-wrap justify-end gap-2">
        <Button variant="secondary" onClick={onVoltar}>
          Voltar
        </Button>
        {editavel ? (
          <Button
            loading={enviando}
            disabled={problemas.length > 0 || envioBloqueado}
            onClick={onGerar}
          >
            Gerar cotacao
          </Button>
        ) : (
          <Button onClick={onIrParaEmissao}>Ir para emissao</Button>
        )}
      </div>
    </div>
  );
}

function BlocoDaParte({
  titulo,
  documento,
  linhas,
}: {
  titulo: string;
  documento: string;
  linhas: EndorsementChange[];
}) {
  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm font-semibold text-ink-900">{titulo}</p>
      <EndorsementInfo
        rotulo="CPF / CNPJ"
        valor={documento ? formatDocument(documento) : ''}
      />
      {linhas.map((linha) => (
        <div key={`${linha.scope}-${linha.field}`} className="min-w-0">
          <p className="text-2xs font-semibold uppercase tracking-wide text-ink-500">
            {rotulosDosCampos[linha.field]}
          </p>
          {linha.changed ? (
            <>
              <p className="text-sm text-ink-500 line-through">
                {valorEmTexto(linha.before) || 'Nao informado'}
              </p>
              <p className="text-sm font-medium text-brand-700">
                {valorEmTexto(linha.after) || 'Em branco'}
              </p>
            </>
          ) : (
            <p className="text-sm text-ink-900">
              {valorEmTexto(linha.before) || 'Nao informado'}
            </p>
          )}
        </div>
      ))}
    </div>
  );
}
